import format from 'date-fns/format';
import isSameDay from 'date-fns/isSameDay';
import startOfDay from 'date-fns/startOfDay';

import { addDays, toDate } from 'bloko/blocks/calendar/datesHelper';

import { parseDate } from 'src/components/LineChart/utils';
import { RectangleChartPoint, RectangleChartIconPoint } from 'src/components/RectangleChart/RectangleChart.types';
import Color from 'src/constants/color';
import VacancyPublicationType from 'src/constants/vacancyPublicationType';
import {
    Publication,
    ConversionStatisticsReport as VacancyConversionStatistics,
} from 'src/models/vacancyConversionStatistics';
import last from 'src/utils/array';

import { ICON_EDIT, ICON_ARROW_UP } from 'src/components/VacancyConversionChart/chart-icons';

const getPublicationColor = (type: string): Color => {
    const PUBLICATION_TO_COLOR: Record<string, Color> = {
        [VacancyPublicationType.Standard]: Color.Gray40,
        [VacancyPublicationType.ZPDemo]: Color.Gray40,
        [VacancyPublicationType.StandardPlus]: Color.Gray60,
        [VacancyPublicationType.ZPPromo]: Color.Gray60,
        [VacancyPublicationType.Premium]: Color.Violet40,
        [VacancyPublicationType.ZPBusiness]: Color.Violet40,
        [VacancyPublicationType.ZPSprint]: Color.Violet60,
        [VacancyPublicationType.ZPAnonymous]: Color.Gray20,
        [VacancyPublicationType.Free]: Color.Gray20,
        [VacancyPublicationType.PFP]: Color.Gray30,
    };
    return PUBLICATION_TO_COLOR[type.toUpperCase()] ?? Color.Violet40;
};

/**
 * Объединять публикации в рамках одного дня, оставляя последнее значение
 * Скорректировать дату начала, не выходить за рамки графика
 */
const normalizePublicationData = (
    data: Publication[],
    interval: { startDate: string; endDate: string }
): Publication[] => {
    const isOneDay = data.length === 1;

    const publicationPoints = data.reduce((acc, cur) => {
        // исключить пограничные значения, выходящие за рамки графика, и период более одного дня
        if (cur.startDate > interval.endDate && !isOneDay) {
            return acc;
        }

        const startDate =
            cur.startDate < interval.startDate || cur.startDate === null ? interval.startDate : cur.startDate;

        if (acc[startDate]) {
            acc[startDate].publicationType = cur.publicationType;
            return acc;
        }

        acc[startDate] = {
            ...cur,
            startDate,
        };
        return acc;
    }, {} as Record<string, Publication>);

    return Object.values(publicationPoints);
};

export const preparePublicationData = (
    data: Publication[],
    interval: { startDate: string; endDate: string },
    trls: (type: VacancyPublicationType) => string
): RectangleChartPoint[] => {
    const publicationPoints: RectangleChartPoint[] = [];

    normalizePublicationData(data, interval).forEach((cur, ind, arr) => {
        const { startDate, publicationType } = cur;
        const nextIndex = ind + 1;
        const endDate = arr[nextIndex] ? arr[nextIndex].startDate : interval.endDate;

        const point = {
            startLineDate: parseDate(startDate) as Date,
            endLineDate: parseDate(endDate) as Date,
            color: getPublicationColor(publicationType),
            title: trls(publicationType.toUpperCase() as VacancyPublicationType),
        };
        publicationPoints.push(point);
    }, {} as Record<string, RectangleChartPoint>);

    return publicationPoints;
};

export const prepareProlongateEventData = (data: Publication[]): RectangleChartIconPoint[] => {
    const uniqPoints = new Set(data.flatMap((p) => p.prolongations));

    return [...uniqPoints].map((dt) => ({
        startDate: new Date(dt),
        icon: ICON_ARROW_UP,
    }));
};

/**
 * Отображает иконки редактирования и поднятия вакансии на оси X
 * Если на один день попадает несколько иконок, то объединяет их
 */
export const prepareXAxisIcons = (
    editPoints: RectangleChartIconPoint[],
    prolongatePoints: RectangleChartIconPoint[]
): RectangleChartIconPoint[] => {
    const sortedPoints = [...editPoints, ...prolongatePoints]
        .map((p) => ({ ...p, startDate: startOfDay(p.startDate) }))
        .sort((a, b) => a.startDate.getTime() - b.startDate.getTime());

    return sortedPoints.reduce((result: RectangleChartIconPoint[], point) => {
        const lastPoint = result[result.length - 1];

        if (isSameDay(point.startDate, lastPoint?.startDate)) {
            lastPoint.icon = [ICON_ARROW_UP, ICON_EDIT];
        } else {
            result.push(point);
        }
        return result;
    }, []);
};

/**
 * Если на последний день графика попадает публикация или услуга, необходимо добавить фейковую точку
 */
export const prepareReportData = (report: VacancyConversionStatistics): VacancyConversionStatistics => {
    const { points, publications, endDate } = report;
    const lastPublication = last(publications);
    const lastPoint = last(points);

    // расширение графика за счет фейковой точки выполняется в случае:
    // есть публикация на endDate графика
    // кол-во точек === 1
    if ((lastPublication?.startDate !== endDate && points.length !== 1) || !lastPoint) {
        return report;
    }

    const lastPointDate = toDate(lastPoint.date);
    const newPointDate = format(addDays(lastPointDate, 1), 'yyyy-MM-dd');
    const newPoint = {
        ...lastPoint,
        date: newPointDate,
        isFake: true,
    };

    return {
        ...report,
        endDate: newPointDate,
        points: [...report.points, newPoint],
    };
};
